// src/components/account/ProfileTab.tsx
import { useState, useEffect, ChangeEvent } from 'react';

interface ProfileData {
  fullName: string;
  email: string;
  phone: string;
  accountType: 'provider' | 'patient';
  businessName?: string;
  address?: string;
  website?: string;
}

const ProfileTab: React.FC = () => {
  const [profileData, setProfileData] = useState<ProfileData>({
    fullName: '',
    email: '',
    phone: '',
    accountType: 'patient',
    businessName: '',
    address: '',
    website: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  // Validation errors
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Fetch user profile from WordPress REST API
  const fetchProfile = async () => {
    const token = localStorage.getItem('token'); // JWT token
    if (!token) {
      console.error('No JWT token found');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch('https://therevulist.com/wp-json/custom/v1/profile', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error('Failed to fetch profile');
      const data = await res.json();

      setProfileData({
        fullName: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        accountType: data.account_type || 'patient',
        businessName: data.business_name || '',
        address: data.address || '',
        website: data.website || '',
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  // Handle input changes
  const handleProfileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' })); // Clear error on change
  };

  // Simple validation
  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9+\-\s()]{7,20}$/;
    const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w-]*)*\/?$/i;

    if (!profileData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!profileData.email || !emailRegex.test(profileData.email)) newErrors.email = 'Valid email required';
    if (profileData.phone && !phoneRegex.test(profileData.phone)) newErrors.phone = 'Valid phone number required';
    if (profileData.accountType === 'provider') {
      if (profileData.website && !urlRegex.test(profileData.website)) newErrors.website = 'Valid URL required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save profile changes
  const handleSaveProfile = async () => {
    if (!validate()) return; // Stop if validation fails

    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch('https://therevulist.com/wp-json/custom/v1/update-profile', {
        method: 'POST', // POST, backend koristi /update-profile
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(profileData),
      });

      if (!res.ok) throw new Error('Failed to save profile');
      setIsEditing(false);
      alert('Profile updated successfully!');
    } catch (err) {
      console.error(err);
      alert('Error saving profile');
    }
  };

  if (loading) return <div>Loading profile...</div>;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Profile Information</h2>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line mr-2"></i>
            Edit Profile
          </button>
        ) : (
          <div className="flex gap-3">
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveProfile}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Save Changes
            </button>
          </div>
        )}
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
            <input
              type="text"
              name="fullName"
              value={profileData.fullName}
              onChange={handleProfileChange}
              disabled={!isEditing}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none disabled:bg-gray-50 disabled:text-gray-500 ${
                errors.fullName ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
              }`}
            />
            {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
            <input
              type="email"
              name="email"
              value={profileData.email}
              onChange={handleProfileChange}
              disabled={!isEditing}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none disabled:bg-gray-50 disabled:text-gray-500 ${
                errors.email ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
              }`}
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
            <input
              type="tel"
              name="phone"
              value={profileData.phone}
              onChange={handleProfileChange}
              disabled={!isEditing}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none disabled:bg-gray-50 disabled:text-gray-500 ${
                errors.phone ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
              }`}
            />
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Account Type</label>
            <input
              type="text"
              value={profileData.accountType === 'provider' ? 'Service Provider' : 'Patient'}
              disabled
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
            />
          </div>
        </div>

        {profileData.accountType === 'provider' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
              <input
                type="text"
                name="businessName"
                value={profileData.businessName}
                onChange={handleProfileChange}
                disabled={!isEditing}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Address</label>
              <input
                type="text"
                name="address"
                value={profileData.address}
                onChange={handleProfileChange}
                disabled={!isEditing}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
              <input
                type="text"
                name="website"
                value={profileData.website}
                onChange={handleProfileChange}
                disabled={!isEditing}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none disabled:bg-gray-50 disabled:text-gray-500 ${
                  errors.website ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                }`}
              />
              {errors.website && <p className="text-red-500 text-sm mt-1">{errors.website}</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfileTab;
